import { useRouter } from 'next/router';
import Head from 'next/head';
import { useEffect, useState } from 'react';
import Navbar from './Navbar';
import Header from './Header';
import { useAuth } from '@/contexts/AuthContext';
import { filterMenuItemsForLevel } from '@/utils/accessControl';

const MOBILE_NAV_BREAKPOINT = 1024;

export default function Layout({ children }) {
    const router = useRouter();
    const currentPath = router.pathname;
    const { accessLevel, accessLoading } = useAuth();
    const pageTitleOverrides = {
        '/notifications': 'Notifications',
        '/profile': 'Profile',
    };

    const menuItems = [
        { path: '/dashboard', label: 'Dashboard', icon: 'home' },
        { path: '/3d-model', label: '3D Model', icon: 'bar_chart' },
        { path: '/insar', label: 'InSAR', icon: 'map' },
        { path: '/sensors', label: 'Sensors', icon: 'data_usage' },
        {
            label: 'PhotoMonitoring',
            children: [
                { path: '/changes', label: 'Change Detection', icon: 'change_circle' },
                { path: '/movements', label: 'Movement Analysis', icon: 'trending_up' },
            ],
        },
        // { path: '/changes', label: 'IRIS Viewer', icon: 'language' },
    ];

    const visibleMenuItems = accessLoading ? menuItems : filterMenuItemsForLevel(menuItems, accessLevel);
    const currentMenuItem = visibleMenuItems.reduce((match, item) => {
        if (match) return match;
        if (item.children) {
            return item.children.find((child) => child.path === router.pathname) || null;
        }
        return item.path === router.pathname ? item : null;
    }, null) || { label: pageTitleOverrides[currentPath] || 'Page' };

    const [isOpen, setIsOpen] = useState(true);
    const [isDesktop, setIsDesktop] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        let previousIsDesktop = window.innerWidth >= MOBILE_NAV_BREAKPOINT;
        setIsDesktop(previousIsDesktop);
        setIsOpen(previousIsDesktop);

        const handleResize = () => {
            const nextIsDesktop = window.innerWidth >= MOBILE_NAV_BREAKPOINT;
            setIsDesktop(nextIsDesktop);
            if (nextIsDesktop !== previousIsDesktop) {
                setIsOpen(nextIsDesktop);
                previousIsDesktop = nextIsDesktop;
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (typeof window !== 'undefined' && window.innerWidth < MOBILE_NAV_BREAKPOINT) {
            setIsOpen(false);
        }
    }, [router.pathname]);

    // toggle menu visibility
    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    const handleMenuNavigate = () => {
        if (typeof window !== "undefined" && window.innerWidth < MOBILE_NAV_BREAKPOINT) {
            setIsOpen(false);
        }
    };

    const showNavbar = isDesktop || isOpen;
    const isCollapsed = isDesktop && !isOpen;

    return (
        <>
            <Head>
                <title>ISABHEL | {currentMenuItem.label}</title>
            </Head>
            <div className="min-h-screen app-shell">
                <Header toggleMenu={toggleMenu} />
                <div className='flex flex-row w-full'>
                    {showNavbar &&
                        <Navbar
                            currentPath={currentPath}
                            menuItems={visibleMenuItems}
                            onNavigate={handleMenuNavigate}
                            collapsed={isCollapsed}
                        />
                    }
                    {/* Main Content */}
                    <main className="flex-1 p-4 lg:p-8 text-(--text)">
                        {children}
                    </main>
                </div>
            </div>
        </>
    );
}
